import { styled } from '@mui/material/styles';
import { Box, Button, Typography, TextField } from '@mui/material';
import { ChangeEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { LocationSearch } from './Location';
import { useOutletContext } from 'react-router';
import { ProfileSetupContext } from './ProfileSetupContext';
import { BackButton, Routes } from '@xolv-mybrightlink/common';

const ProfileTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    backgroundColor: '#fff',
    borderRadius: '12px',
  },
  '& fieldset': {
    borderRadius: '12px',
  },
});

const ProfileButton = styled(Button)({
  '& .MuiButton-root': {
    mt: 3,
    mb: 4,
    flex: 1,
    pt: 3.5,
    pb: 2.5,
    borderRadius: '30px',
  },
  '&.MuiButtonBase-root.Mui-disabled': {
    color: '#fff',
    backgroundColor: '#b1b5bd',
  },
});

export function ProfileSetupBasic() {
  const navigate = useNavigate();

  const { profile, updateProfile } = useOutletContext<ProfileSetupContext>();

  const nextStep =
    !!profile &&
    Boolean(profile.formattedAddress) &&
    Boolean(profile.biography);

  const intl = useIntl();
  const requiredError = useMemo(
    () =>
      intl.formatMessage({
        id: 'common.form.field.required',
      }),
    [intl],
  );

  return (
    <Box sx={{ pb: 5 }}>
      <Box>
        <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
          <FormattedMessage id='profile.personal-info.title' />
        </Typography>

        <Typography variant='body1' sx={{ mt: 2.5, color: '#898f9b' }}>
          <FormattedMessage id='profile.interests.hint' />
        </Typography>

        <Typography
          variant='body1'
          sx={{ mt: 10, fontWeight: 500, color: '#3b4559' }}
        >
          <FormattedMessage id='profile.personal-info.first-name' />
        </Typography>

        <ProfileTextField
          sx={{ width: 0.7, mt: 2.5 }}
          defaultValue={profile?.firstName || ''}
          error={!profile?.firstName}
          helperText={profile?.firstName ? '' : requiredError}
          onInput={(event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            updateProfile('firstName', () => value);
          }}
        />

        <Typography
          variant='body1'
          sx={{ mt: 10, fontWeight: 500, color: '#3b4559' }}
        >
          <FormattedMessage id='profile.personal-info.last-name' />
        </Typography>

        <ProfileTextField
          sx={{ width: 0.7, mt: 2.5 }}
          defaultValue={profile?.lastName || ''}
          error={!profile?.lastName}
          helperText={profile?.lastName ? '' : requiredError}
          onInput={(event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            updateProfile('lastName', () => value);
          }}
        />

        <Typography
          variant='body1'
          sx={{ mt: 10, fontWeight: 500, color: '#3b4559' }}
        >
          <FormattedMessage id='profile.location' />
        </Typography>

        <Box sx={{ width: 0.7, mt: 2.5 }}>
          <LocationSearch
            value={profile?.formattedAddress || ''}
            onChange={(value: string) => {
              updateProfile('formattedAddress', () => value);
            }}
          />
        </Box>

        <Typography
          variant='body1'
          sx={{ mt: 5, fontWeight: 500, color: '#3b4559' }}
        >
          <FormattedMessage id='profile.personal-info.bio' />
        </Typography>

        <Typography
          variant='subtitle2'
          sx={{ mt: 2.5, color: '#898f9b', mb: 1 }}
        >
          <FormattedMessage id='profile.personal-info.bio.hint' />
        </Typography>

        <ProfileTextField
          multiline
          minRows={6}
          maxRows={6}
          sx={{ width: 0.7, mt: 2.5 }}
          defaultValue={profile?.biography || ''}
          onInput={(event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            updateProfile('biography', () => value);
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', mt: 20, gap: 8, width: 0.5, mx: 'auto' }}>
        <BackButton size='large' />
        <ProfileButton
          variant='contained'
          size='large'
          sx={{ px: 35, py: 4.5, borderRadius: 22 }}
          disableElevation
          disabled={!nextStep}
          disableRipple={true}
          onClick={() => {
            navigate(Routes.profile.interests);
          }}
        >
          <FormattedMessage id='common.next' />
        </ProfileButton>
      </Box>
    </Box>
  );
}
